import clsx from 'clsx';
import { isEmpty, keys, uniq } from 'lodash';
import { bool } from 'prop-types';
import { useContext, useEffect, useMemo } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';

import {
  Breadcrumb,
  GiftcardsList,
  HeroBanner,
  Icon,
  LeisuresList,
  ProductsContext,
  ProductsFilters,
  SanitizedHtml,
  Skeleton,
  StrapiImg,
  TravelsList,
  useProductsContext
} from 'components';
import {
  PAGE_TYPES,
  giftcardDefaultValues,
  leisureDefaultValues,
  sportDefaultValues,
  travelDefaultValues
} from 'constants';
import { useFetch, useSafeState } from 'hooks';
import { routes } from 'router';
import { appService } from 'services';

import leisureHero from 'assets/img/leisure-park.jpeg';
import travelHero from 'assets/img/travel-hero.png';
import 'assets/styles/components/pages/results-page.scss';

const ResultsPage = ({ fromHomepage = false }) => {
  const { t } = useTranslation();
  const { pageType, sportCategoryId } = useProductsContext();
  const [searchParams, setSearchParams] = useSearchParams();

  const [showFilters, setShowFilters] = useSafeState(false);

  const { handleSubmit, control, trigger, register, getValues, setValue, reset, formState } = useForm({
    mode: 'all',
    defaultValues: { ...travelDefaultValues, ...leisureDefaultValues, ...sportDefaultValues, ...giftcardDefaultValues }
  });
  const { tourOperatorFilters } = useContext(ProductsContext);
  const { call: heroCall, data: heroResponse, loading: loadingHero } = useFetch({ filterUserCompany: true });

  const heroData = useMemo(() => {
    const heroDatas = heroResponse?.data?.[0];
    if (isEmpty(heroDatas)) {
      // Display default hero image and title if there are no content in Strapi
      return {
        heroTitle: t('yourResults'),
        heroImage: {
          frontUrl: pageType === PAGE_TYPES.travels ? travelHero : leisureHero
        }
      };
    } else {
      return heroDatas;
    }
  }, [heroResponse, pageType]);

  const breadcrumbs = useMemo(() => {
    let routeName = 'home';
    if (pageType === PAGE_TYPES.travels) routeName = 'travelHomePage';
    if (pageType === PAGE_TYPES.leisures) routeName = 'leisureHomePage';
    if (pageType === PAGE_TYPES.sport) routeName = 'sportHomePage';
    if (pageType === PAGE_TYPES.giftcards) routeName = 'giftcardHomePage';

    const crumbs = [
      { url: routes[routeName].path, label: t('breadcrumb.home') },
      { url: routes[routeName].path, label: t(`breadcrumb.${pageType}`) }
    ];

    if (pageType === PAGE_TYPES.travels) {
      const tourOperatorName = tourOperatorFilters?.find(
        (item) => item.id === searchParams.get('tourOperatorId')
      )?.label;
      tourOperatorName ? crumbs.push({ label: tourOperatorName }) : null;
    } else {
      crumbs.push({ label: t('breadcrumb.search') });
    }

    return crumbs;
  }, [pageType, tourOperatorFilters, searchParams.get('tourOperatorId')]);

  // -----------------------------------------------------------------------------------------------------
  // Lifecycle hooks
  // -----------------------------------------------------------------------------------------------------

  useEffect(() => {
    if (!fromHomepage) {
      prefillFilters();
      setDefaultParams();
    }
  }, [pageType, searchParams, sportCategoryId, fromHomepage]);

  useEffect(() => {
    !fromHomepage && getHeroContent();
  }, [fromHomepage, pageType, searchParams.get('tourOperatorId')]);

  // -----------------------------------------------------------------------------------------------------
  // Methods
  // -----------------------------------------------------------------------------------------------------

  const prefillFilters = () => {
    const searchParamsObj = Object.fromEntries(searchParams.entries());

    keys(searchParamsObj).forEach((key) => {
      const value = searchParamsObj[key];
      const isSelectKey = ['country', 'departure', 'tourOperatorId', 'place'].includes(key);
      const isDateKey = key === 'beginDate' || key === 'endDate';

      if (isSelectKey) {
        setValue(key, { value, id: value, ...(value === 'touloisirs' && { label: 'Touloisirs' }) });
      } else if (isDateKey) {
        key === 'beginDate' && setValue('dates', [new Date(value), getValues('dates')[1]]);
        key === 'endDate' && setValue('dates', [getValues('dates')[0], new Date(value)]);
      } else {
        const newValue = uniq(value.split(','));
        if (pageType === PAGE_TYPES.leisures && (key === 'category' || key === 'subCategory')) {
          return;
        } else if (key === 'subsidizedOffers') {
          setValue(key, value === 'oui' ? true : false);
        } else {
          setValue(key, newValue);
        }
      }
    });

    // Set leisure/sport category and subCategory
    if (
      (pageType === PAGE_TYPES.leisures || pageType === PAGE_TYPES.sport) &&
      searchParamsObj?.category &&
      searchParamsObj?.subCategory
    ) {
      const categories = searchParamsObj.category.split(',');
      const subCategories = searchParamsObj.subCategory.split(',');

      const leisureCategory = categories.reduce((acc, category, index) => {
        acc[category] = subCategories[index].split('-');
        return acc;
      }, {});
      setValue(pageType === PAGE_TYPES.leisures ? 'leisureCategory' : 'sportCategory', leisureCategory, {
        shouldValidate: true,
        shouldDirty: true,
        shouldTouch: true
      });
    }
  };

  const getHeroContent = () => {
    heroCall({
      service: appService.getStrapiServiceName(),
      method: 'get',
      path: '/api/result-pages',
      query: {
        filters: {
          page_type: { name: { $eq: pageType } },
          // Tour operator are not necessary for leisures
          ...(pageType === PAGE_TYPES.travels && { tour_operator_slug: { $eq: searchParams.get('tourOperatorId') } })
        }
      }
    });
  };

  const setDefaultParams = () => {
    if ([PAGE_TYPES.leisures, PAGE_TYPES.giftcards].includes(pageType)) return;
    const allowedResultTypes = [PAGE_TYPES.travels, PAGE_TYPES.leisures, PAGE_TYPES.sport, PAGE_TYPES.giftcards];

    if (pageType === PAGE_TYPES.sport && !searchParams.get('category') && sportCategoryId) {
      // Set sport category if it's not present in the url
      setSearchParams({ ...searchParams, type: PAGE_TYPES.sport, category: sportCategoryId });
    } else if ((pageType && !allowedResultTypes.includes(pageType)) || !pageType) {
      /**
       * Ensure that page type is either travels, leisures or sport
       * If no page type is present in the url, set it to travels by default
       */
      const params = {
        ...(!pageType && { type: PAGE_TYPES.travels }),
        ...(!pageType && !searchParams.get('tourOperatorId') && { tourOperatorId: 'touloisirs' })
      };

      setSearchParams(params);
    }
  };

  const formProviderValues = {
    handleSubmit,
    control,
    trigger,
    register,
    getValues,
    setValue,
    formState,
    reset
  };

  return (
    <FormProvider {...formProviderValues}>
      <div className="results-page">
        {!fromHomepage && (
          <>
            <div className="hidden md:block">
              <HeroBanner
                title={heroData?.heroTitle}
                image={heroData?.heroImageDesktop}
                mobileImage={heroData?.heroImageMobile}
                loading={loadingHero}
              />
            </div>

            <div className="main-container mt-26 lg:mt-12">
              {isEmpty(heroResponse?.data?.[0]) && (
                <h1 className="h1 underlined-blue md:hidden">{heroData?.heroTitle}</h1>
              )}

              <div className="hidden md:block md:mb-4">
                <Breadcrumb breadcrumbItems={breadcrumbs} />
              </div>

              {/* Hero content */}
              <>
                {loadingHero && (
                  <>
                    <Skeleton type="text" classNames="w-full" />
                    <Skeleton type="text" classNames="w-full" />
                    <Skeleton type="text" classNames="w-[75%]" />
                  </>
                )}

                {!loadingHero && (
                  <>
                    <div className="flex-center-between mb-8">
                      {heroData?.pageTitle && <h2 className="h1 underlined-blue">{heroData?.pageTitle}</h2>}

                      <StrapiImg image={heroData?.logo} className="w-40 h-12 object-contain" />
                    </div>

                    {heroData?.intro && <SanitizedHtml content={heroData.intro} className="mb-8" />}
                  </>
                )}
              </>
            </div>
          </>
        )}

        {/* Search filters */}
        <div className="main-container pb-12 grid grid-cols-12 gap-5 2xl:gap-12">
          <div className="col-span-12 lg:col-span-4 2xl:col-span-3">
            <div
              className={clsx(
                'filters-container',
                showFilters && 'show-filters',
                pageType === PAGE_TYPES.giftcards && 'no-scroll'
              )}
            >
              <ProductsFilters setShowFilters={setShowFilters} showFilters={showFilters} fromHomepage={fromHomepage} />
            </div>
          </div>

          {/* Results */}
          <div className={clsx('relative', showFilters ? 'hidden' : 'col-span-12 lg:col-span-8 2xl:col-span-9')}>
            <button
              type="button"
              className="btn-white absolute -top-2 right-0 rounded-xl px-4 flex-center-between gap-5 lg:hidden"
              onClick={() => setShowFilters(true)}
            >
              <span className="text-gray-700">{t('filters')}</span>
              <Icon name="filterBars" classes="w-4 h-4 shrink-0" />
            </button>

            {pageType === PAGE_TYPES.travels && <TravelsList />}
            {[PAGE_TYPES.leisures, PAGE_TYPES.sport].includes(pageType) && <LeisuresList />}
            {pageType === PAGE_TYPES.giftcards && <GiftcardsList />}
          </div>
        </div>
      </div>
    </FormProvider>
  );
};

ResultsPage.propTypes = {
  fromHomepage: bool
};

export default ResultsPage;
